import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { Field, FieldArray, reduxForm, getFormValues, isDirty } from 'redux-form';

import AppStore from 'af-root/store';

import type { BillingCodeVM } from 'ab-viewModels/billingCode.viewModel';
import type { SubjobVM } from 'ab-viewModels/workRequest/jobUpsert.viewModel';
import type JobWorkSummaryVM from 'ab-viewModels/workRequest/jobWorkSummary.viewModel';
import type JobWorkSummaryWorkOrderVM from 'ab-viewModels/workRequest/jobWorkSummaryWorkOrder.viewModel';

import type JobWorkSummaryRM from 'ab-requestModels/workRequest/jobWorkSummaryUpsert.requestModel';

import { filterMap } from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';
import { convertUnits } from '@acceligentllc/shared/utils/unitConversion';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import { CompoundUnitEnum, QuantityUnitLabel, QuantityUnitMap } from '@acceligentllc/shared/enums/quantityUnit';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { UnitClasses } from '@acceligentllc/shared/enums/unitConversion';

import type { RootState } from 'af-reducers';

import { dollarFormatter } from 'af-utils/format.util';

import { JobWorkSummaryDataType } from 'ab-enums/jobWorkSummaryDataType.enum';

import * as FORMS from 'af-constants/reduxForms';

import { MAX_BILLABLE_WORK_DEFINITION_FIELDS, MAX_BILLABLE_WORK_INFORMATION_FIELDS } from 'ab-common/constants/value';

import { Button } from '@acceligentllc/storybook';

import DateInput from 'af-fields/DateInput';
import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';
import Text from 'af-fields/Text';
import Textarea from 'af-fields/Textarea';

import ColorSquare from 'af-components/ColorSquare';
import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import * as JobActions from 'af-actions/jobs';

import type { SubjobBillingCodes, SubjobWorkOrders } from '..';
import { FormModel } from '../FormModel';
import AddBillableWorkModalDefinitionField from './AddBillableWorkModalDefinitionField';
import AddBillableWorkModalInformationalField from './AddBillableWorkModalInformationalField';
import { renderEquipmentFieldFromJsonForm } from './helpers';
import styles from './styles.module.scss';

interface BillingCodeDropdownOption {
	id: number;
	customerId: string;
	unitPrice: Nullable<string>;
	unit: Nullable<string>;
	description: string;
	group: Nullable<string>;
	groupColor: Nullable<ColorPalette>;
	deletedAt: Nullable<Date>;
}

type BillingCodeDropdownOptionWithUnitData = BillingCodeDropdownOption & {
	formUnit: Nullable<string>;
	formQuantity: Nullable<number>;
};

interface QuantityDropdownOption {
	id: QuantityUnitType;
	label: string;
}

type OriginalUnits = {
	[key: string]: Nullable<string>;
};

const getFormData = getFormValues(FORMS.WORK_SUMMARY_BILLABLE_WORK);

const _delimitersRegEx = new RegExp('\/|-| ');
const _findCommaOutsideCurlyBracketsRegex = new RegExp(/[,;]\s*(?![^{}]*})/);

const _filterBillingCodes = (_option: BillingCodeVM, _searchText: string) => {
	_searchText.trim();
	const splitSearch = _searchText.toLowerCase().split(_delimitersRegEx).join('');
	const _customerIdToLowerCase = _option.customerId.toLowerCase().split(_delimitersRegEx).join('');
	const _descriptionToLowerCase = _option.description.toLowerCase().split(_delimitersRegEx).join('');

	if (!!splitSearch && _customerIdToLowerCase.includes(splitSearch) || _descriptionToLowerCase.includes(splitSearch)) {
		return true;
	}

	return false;
};

const _renderBillingCodeMenuItem = (_bc: BillingCodeDropdownOptionWithUnitData) => {
	const convertedUnit = _bc.formQuantity && _bc.formUnit && _bc.unit
		? convertUnits(_bc.formQuantity, QuantityUnitMap[_bc.formUnit], QuantityUnitMap[_bc.unit])
		: null;
	const convertedUnitLabel = convertedUnit && `${_bc.formQuantity} ${_bc.formUnit} = ${parseFloat(Number(convertedUnit).toFixed(4)).toString()} ${_bc.unit}`;
	const perUnitPriceLabel = <div>{`$ ${_bc.unitPrice} per ${_bc.unit}`}{_bc.groupColor && <ColorSquare color={_bc.groupColor} isSmall={true} />}{` ${_bc.group ?? ''}`}</div>;

	return (
		<div className={styles['add-billable-work-modal__billing-code-dropdown-item']}>
			<b>{_bc.customerId}</b>
			<span><b>{perUnitPriceLabel}</b></span>
			{convertedUnit && (_bc.unit !== _bc.formUnit)
				? <p className={styles.dropdown_card_subtitle}>{convertedUnitLabel}</p>
				: ''
			}
			<p>{_bc.description}</p>
		</div>
	);
};

const _renderSubjobMenuItem = (_subjob: SubjobVM) => {
	return (
		<div className={styles['add-billable-work-modal__billing-code-dropdown-item']}>
			<b>{_subjob.jobCode}</b>
		</div>
	);
};

const _renderSelectedDropdownMenuItem = (_billingCode: BillingCodeDropdownOption) => <span key={_billingCode.id}>{_billingCode.customerId}</span>;

const _renderSelectedSubjob = (_subjob: SubjobVM) => <span key={_subjob.id}>{_subjob.jobCode}</span>;

const _renderNoBillingCodeMenuItem = (item) => <span>{item.label}</span>;

const _evaluateInfoFieldUnits = (_units: Nullable<string>) => {
	if (_units?.trim()) {
		return _units.split(_findCommaOutsideCurlyBracketsRegex);
	}

	return [];
};

const _resolveInformationalFields = (
	informationalFields: FormModel['informationalFields'],
	areInformationFieldsDirty: boolean[],
	currentlyEditedRow: Nullable<JobWorkSummaryVM>
) => {
	return [
		(areInformationFieldsDirty[0] ? informationalFields?.[0]?.values?.split('; ') : currentlyEditedRow?.informationField1Value?.split(_findCommaOutsideCurlyBracketsRegex)) ?? [],
		(areInformationFieldsDirty[1] ? informationalFields?.[1]?.values?.split('; ') : currentlyEditedRow?.informationField2Value?.split(_findCommaOutsideCurlyBracketsRegex)) ?? [],
		(areInformationFieldsDirty[2] ? informationalFields?.[2]?.values?.split('; ') : currentlyEditedRow?.informationField3Value?.split(_findCommaOutsideCurlyBracketsRegex)) ?? [],
		(areInformationFieldsDirty[3] ? informationalFields?.[3]?.values?.split('; ') : currentlyEditedRow?.informationField4Value?.split(_findCommaOutsideCurlyBracketsRegex)) ?? [],
	];
};

const _formatTypeField = (type: Nullable<string>) => {
	return (value) => {
		if (value && type === ReportBlockFieldEnum.Type.EQUIPMENT) {
			return renderEquipmentFieldFromJsonForm(value);
		}
		return value ?? '';
	};
};

const compoundKeys = Object.keys(CompoundUnitEnum);
const quantityUnitOptions: QuantityDropdownOption[] = filterMap(
	Object.keys(QuantityUnitLabel),
	(_unit: QuantityUnitType) => !compoundKeys.includes(_unit),
	(_unit: QuantityUnitType) => ({ id: _unit, label: QuantityUnitLabel[_unit] })
);

type OwnProps = {
	showModal: boolean;
	closeModal: () => void;
	billingCodes: BillingCodeVM[];
	workOrders: JobWorkSummaryWorkOrderVM[];
	jobId: number;
	refreshTable: () => void;
	refreshTableHeader: () => void;
	currentlyEditedRow: Nullable<JobWorkSummaryVM>;
	isProject: boolean;
	subjobs: SubjobVM[];
	subjobBillingCodes: SubjobBillingCodes[];
	subjobWorkOrders: SubjobWorkOrders[];
};

type Props = OwnProps & InjectedFormProps<FormModel> & ConnectedProps<typeof connector>;

const AddBillableWorkModal: React.FC<Props> = (props) => {
	const {
		closeModal,
		showModal,
		billingCodes,
		workOrders,
		initialize,
		destroy,
		change,
		formDataUnit,
		formDataBillingCodeId,
		formDataQuantity,
		formDataRevenue,
		formDataUnitPrice,
		formDataCustomerId,
		formDataGroup,
		formDataType,
		formDataTypeValue,
		valid,
		createJobWorkSummary,
		handleSubmit,
		jobId,
		refreshTable,
		currentlyEditedRow,
		editJobWorkSummary,
		dirty,
		isProject,
		subjobs,
		subjobBillingCodes,
		subjobWorkOrders,
		formWorkRequestId,
		refreshTableHeader,
		originalUnits,
	} = props;

	const billingCodeOptions = React.useMemo(
		() => {
			const _billingCodes = !!subjobBillingCodes.length
				? subjobBillingCodes.find((_sjbc) => _sjbc.workRequestId === (formWorkRequestId ?? jobId))?.billingCodes ?? []
				: billingCodes;

			return !!formDataUnit ?
				_billingCodes.reduce((_acc: BillingCodeDropdownOption[], _bc: BillingCodeVM) => {
					const _bcUnit = QuantityUnitMap[_bc.unit];
					const _formUnit = QuantityUnitMap[formDataUnit.replace('/', '_')];
					if (_bcUnit && _formUnit && (
						(UnitClasses[_formUnit] && (UnitClasses[_bcUnit] === UnitClasses[_formUnit])) // if there is a conversion rate
						|| (_formUnit === _bcUnit) // if units are the same
					)) {
						_acc.push({
							customerId: _bc.customerId,
							id: _bc.id,
							description: _bc.description,
							unitPrice: _bc.unitPrice,
							group: _bc.groupName,
							unit: QuantityUnitMap[_bc.unit] ?? null,
							deletedAt: _bc.deletedAt,
							groupColor: _bc.groupColor,
						});
					}
					return _acc;
				}, [])
				: _billingCodes;
		}, [billingCodes, formDataUnit, formWorkRequestId, jobId, subjobBillingCodes]
	);

	const selectedBillingCode = React.useMemo(() =>
		billingCodeOptions.find((_bco) => _bco.id === formDataBillingCodeId),
		[billingCodeOptions, formDataBillingCodeId]
	);

	const activeBillingCodes = React.useMemo(() =>
		billingCodeOptions.filter((_bco) => !_bco.deletedAt || _bco.id === selectedBillingCode?.id),
		[billingCodeOptions, selectedBillingCode?.id]
	);

	const filteredQuantityUnitOptions = React.useMemo(
		() => {
			const selectedUnit = selectedBillingCode?.unit;
			if (!selectedUnit) {
				return quantityUnitOptions;
			}
			return quantityUnitOptions.filter((unit) => {
				return (UnitClasses[unit.id] && (UnitClasses[unit.id] === UnitClasses[selectedUnit])) || (unit.id === selectedUnit);
			});
		}, [selectedBillingCode]
	);

	const workOrderOptions = React.useMemo(() => {
		const _workOrders = !!subjobWorkOrders.length
			? subjobWorkOrders.flatMap((sjwo) => sjwo.workOrders)
			: workOrders;

		const workOrderList = [..._workOrders];

		if (!!currentlyEditedRow?.workOrderId) {
			let originalWorkOrder: Nullable<JobWorkSummaryWorkOrderVM> = null;
			for (const sjWos of subjobWorkOrders) {
				originalWorkOrder = sjWos.workOrders.find((_wo) => _wo.id === currentlyEditedRow.workOrderId) ?? null;
				if (originalWorkOrder && workOrderList.every((_wo) => _wo.id !== originalWorkOrder!.id)) {
					workOrderList.unshift(originalWorkOrder);
					break;
				}
			}
		}

		return workOrderList.map((_wo) => ({ id: _wo.id, label: _wo.workOrderCode }));
	}, [subjobWorkOrders, workOrders, currentlyEditedRow?.workOrderId]);

	const convertedFormUnitPrice = React.useMemo(() => {
		if (!selectedBillingCode ?? (selectedBillingCode?.unitPrice === undefined || selectedBillingCode?.unitPrice === null)) {
			return null;
		}
		const newUnitPrice = formDataUnit && selectedBillingCode?.unit
			? convertUnits(+selectedBillingCode.unitPrice, QuantityUnitMap[formDataUnit], QuantityUnitMap[selectedBillingCode.unit])
			: selectedBillingCode.unitPrice;

		return newUnitPrice ? +newUnitPrice : +selectedBillingCode.unitPrice;
	}, [selectedBillingCode, formDataUnit]);

	const onBillingCodeChange = React.useCallback((_billingCode: BillingCodeDropdownOption) => {
		const convertedQuantity = formDataUnit && _billingCode.unit
			? convertUnits(formDataQuantity ?? 0, QuantityUnitMap[formDataUnit], QuantityUnitMap[_billingCode.unit])
			: formDataQuantity;

		change('billingCodeId', _billingCode.id);
		change('billingCodeUnit', _billingCode.unit);
		change('customerId', _billingCode.customerId);
		change('group', _billingCode.group);
		change('unitPrice', _billingCode.unitPrice);
		change('description', _billingCode.description);
		change('revenue', +(_billingCode.unitPrice ?? 0) * (convertedQuantity ?? formDataQuantity ?? 0));
		if (!formDataUnit && _billingCode.unit) {
			change('unit', QuantityUnitMap[_billingCode.unit]);
		}
	}, [formDataQuantity, formDataUnit, change]);

	const onQuantityChange = React.useCallback((quantity: number) => {
		const convertedQuantity = formDataUnit && selectedBillingCode?.unit
			? convertUnits(+quantity ?? 0, QuantityUnitMap[formDataUnit], QuantityUnitMap[selectedBillingCode.unit])
			: quantity;

		change('revenue', +(formDataUnitPrice ?? 0) * (convertedQuantity ?? +quantity ?? 0));
	}, [formDataUnitPrice, formDataUnit, selectedBillingCode, change]);

	const onUnitChange = React.useCallback((unit: QuantityDropdownOption) => {
		const convertedQuantity = convertUnits(formDataQuantity ?? 0, unit?.id, selectedBillingCode?.unit as QuantityUnitType);

		change('revenue', +(formDataUnitPrice ?? 0) * (convertedQuantity ?? formDataQuantity ?? 0));
	}, [formDataUnitPrice, formDataQuantity, selectedBillingCode, change]);

	const onBillingCodeClear = React.useCallback(() => {
		change('customerId', null);
		change('billingCodeId', null);
		change('billingCodeUnit', null);
		change('group', null);
		change('description', null);
		change('revenue', null);
		change('unitPrice', null);
	}, [change]);

	const onUnitClear = React.useCallback(() => {
		change('unit', null);
		change('revenue', +(formDataUnitPrice ?? 0) * (formDataQuantity ?? 0));
	}, [change, formDataUnitPrice, formDataQuantity]);

	const onWorkOrderClear = React.useCallback(() => {
		change('workOrderId', null);
	}, [change]);

	const renderBillingCodePlaceholder = React.useCallback(() =>
		<span className={styles['add-billable-work-modal__billing-code-placeholder']}>
			Add Billing Code to see
		</span>
		, []);

	const onJobChange = React.useCallback((_jobId: React.ChangeEvent<HTMLInputElement>) => {
		change('workRequestId', _jobId);
		// Clean billing code and work Order information
		onBillingCodeClear();
	}, [change, onBillingCodeClear]);

	const renderBillingCodeDropdown = React.useCallback(() => {
		if (!!billingCodeOptions?.length) {
			return (
				<Field
					component={Dropdown}
					filterable={true}
					filterBy={_filterBillingCodes}
					fixed={false}
					isClearable={true}
					label="Billing Code *"
					name="billingCodeId"
					onClear={onBillingCodeClear}
					onValueChange={onBillingCodeChange}
					options={activeBillingCodes ?? []}
					placeholder="Add Billing Code"
					renderMenuItem={_renderBillingCodeMenuItem}
					renderSelected={_renderSelectedDropdownMenuItem}
					valueKey="id"
					withCaret={true}
				/>
			);
		}

		return (
			<Field
				component={Dropdown}
				id="billingCodeId"
				label="Billing Code *"
				name="billingCodeId"
				options={[{ disabled: true, label: 'No billing codes found.' }]}
				placeholder="No billing codes found."
				renderMenuItem={_renderNoBillingCodeMenuItem}
				withCaret={true}
			/>
		);
	}, [billingCodeOptions?.length, onBillingCodeClear, onBillingCodeChange, activeBillingCodes]);

	const isDefinitionOrInformationFieldDirty = React.useCallback((fieldType: 'definition' | 'informational') => {
		const areDirtyValues: boolean[] = [];
		const MAX_LOOP_COUNT = fieldType === 'definition' ? MAX_BILLABLE_WORK_DEFINITION_FIELDS : MAX_BILLABLE_WORK_INFORMATION_FIELDS;
		for (let index = 0; index < MAX_LOOP_COUNT; index++) {
			const fieldDirty = isDirty(FORMS.WORK_SUMMARY_BILLABLE_WORK)(AppStore.getStore().getState(), `${fieldType}Fields[${index}].${fieldType === 'informational' ? 'values' : 'value'}`);
			areDirtyValues.push(fieldDirty);
		}
		return areDirtyValues;
	}, []);

	const onSubmit = React.useCallback(async (form: FormModel) => {

		if (
			!form.billingCodeId
			|| !form.customerId
			|| !form.unit
			|| !form.unitPrice
			|| !form.description
			|| !form.quantity
			|| !form.work
			|| !form.type
		) {
			return;
		}

		const areDefinitionFieldsDirty = isDefinitionOrInformationFieldDirty('definition');
		const areInformationFieldsDirty = isDefinitionOrInformationFieldDirty('informational');
		const informationFieldValues = _resolveInformationalFields(form.informationalFields, areInformationFieldsDirty, currentlyEditedRow);
		const informationFieldUnits = [
			_evaluateInfoFieldUnits(form.informationalFields?.[0]?.units),
			_evaluateInfoFieldUnits(form.informationalFields?.[1]?.units),
			_evaluateInfoFieldUnits(form.informationalFields?.[2]?.units),
			_evaluateInfoFieldUnits(form.informationalFields?.[3]?.units),
		];

		const isTypeValueFieldDirty = isDirty(FORMS.WORK_SUMMARY_BILLABLE_WORK)(AppStore.getStore().getState(), 'typeValue');
		const newTypeValue = isTypeValueFieldDirty
			?
			((currentlyEditedRow && currentlyEditedRow.dataType !== JobWorkSummaryDataType.STANDALONE) ? form.typeValue : form.type)
			:
			((currentlyEditedRow && currentlyEditedRow.dataType !== JobWorkSummaryDataType.STANDALONE)
				? currentlyEditedRow.typeValue
				: currentlyEditedRow?.type ?? null);

		const informationFieldNames = [
			informationFieldValues[0].length > 1
				? Array(informationFieldValues[0].length).fill(form.informationalFields?.[0]?.name ?? '')
				: [form.informationalFields?.[0]?.name ?? ''],
			informationFieldValues[1].length > 1
				? Array(informationFieldValues[1].length).fill(form.informationalFields?.[1]?.name ?? '')
				: [form.informationalFields?.[1]?.name ?? ''],
			informationFieldValues[2].length > 1
				? Array(informationFieldValues[2].length).fill(form.informationalFields?.[2]?.name ?? '')
				: [form.informationalFields?.[2]?.name ?? ''],
			informationFieldValues[3].length > 1
				? Array(informationFieldValues[3].length).fill(form.informationalFields?.[3]?.name ?? '')
				: [form.informationalFields?.[3]?.name ?? ''],
		];

		const definitionFieldUnits = [
			form.definitionFields?.[0]?.unit?.trim(),
			form.definitionFields?.[1]?.unit?.trim(),
			form.definitionFields?.[2]?.unit?.trim(),
			form.definitionFields?.[3]?.unit?.trim(),
		];

		const jobWorkSummaryRM: JobWorkSummaryRM = {
			id: !!currentlyEditedRow ? currentlyEditedRow.id : undefined,
			billingCodeId: form.billingCodeId,
			billingCodeUnit: form.billingCodeUnit,
			customerId: form.customerId,

			definitionField1Name: currentlyEditedRow?.definitionField1Name ?? null,
			definitionField1Value: areDefinitionFieldsDirty[0]
				? (form.definitionFields?.[0]?.value ?? null)
				: (currentlyEditedRow?.definitionField1Value ?? null),
			definitionField1Unit: definitionFieldUnits[0] ? definitionFieldUnits[0] : null,

			definitionField2Name: currentlyEditedRow?.definitionField2Name ?? null,
			definitionField2Value: areDefinitionFieldsDirty[1]
				? (form.definitionFields?.[1]?.value ?? null)
				: (currentlyEditedRow?.definitionField2Value ?? null),
			definitionField2Unit: definitionFieldUnits[1] ? definitionFieldUnits[1] : null,

			definitionField3Name: currentlyEditedRow?.definitionField3Name ?? null,
			definitionField3Value: areDefinitionFieldsDirty[2]
				? (form.definitionFields?.[2]?.value ?? null)
				: (currentlyEditedRow?.definitionField3Value ?? null),
			definitionField3Unit: definitionFieldUnits[2] ? definitionFieldUnits[2] : null,

			definitionField4Name: currentlyEditedRow?.definitionField4Name ?? null,
			definitionField4Value: areDefinitionFieldsDirty[3]
				? (form.definitionFields?.[3]?.value ?? null)
				: (currentlyEditedRow?.definitionField4Value ?? null),
			definitionField4Unit: definitionFieldUnits[3] ? definitionFieldUnits[3] : null,

			informationField1Name: informationFieldNames[0],
			informationField1Value: informationFieldValues[0],
			informationField1Unit: informationFieldUnits[0].length === 1
				? Array(informationFieldValues[0].length).fill(informationFieldUnits[0][0].trim())
				: informationFieldUnits[0].map((_unit) => _unit.trim()),

			informationField2Name: informationFieldNames[1],
			informationField2Value: informationFieldValues[1],
			informationField2Unit: informationFieldUnits[1].length === 1
				? Array(informationFieldValues[1].length).fill(informationFieldUnits[1][0].trim())
				: informationFieldUnits[1].map((_unit) => _unit.trim()),

			informationField3Name: informationFieldNames[2],
			informationField3Value: informationFieldValues[2],
			informationField3Unit: informationFieldUnits[2].length === 1
				? Array(informationFieldValues[2].length).fill(informationFieldUnits[2][0].trim())
				: informationFieldUnits[2].map((_unit) => _unit.trim()),

			informationField4Name: informationFieldNames[3],
			informationField4Value: informationFieldValues[3],
			informationField4Unit: informationFieldUnits[3].length === 1
				? Array(informationFieldValues[3].length).fill(informationFieldUnits[3][0].trim())
				: informationFieldUnits[3].map((_unit) => _unit.trim()),

			description: form.description,
			group: form.group,
			invoiceId: null,
			quantity: form.quantity,
			startDate: TimeUtils.formatDate(form.date, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			type: form.type,
			typeValue: newTypeValue,
			unit: form.unit,
			work: form.work,
			workOrderId: form.workOrderId,
			fieldReportId: form.fieldReportId,
			unitPrice: form.unitPrice,
			comment: form.comment,
			workRequestId: form.workRequestId ?? jobId,
			revenue: form.revenue,
		};

		if (currentlyEditedRow) {
			await editJobWorkSummary(jobWorkSummaryRM);
		} else {
			await createJobWorkSummary(jobWorkSummaryRM);
		}
		closeModal();
		refreshTable();
		refreshTableHeader();
	}, [
		isDefinitionOrInformationFieldDirty,
		currentlyEditedRow,
		jobId,
		closeModal,
		refreshTable,
		refreshTableHeader,
		editJobWorkSummary,
		createJobWorkSummary,
	]);

	const currentSubjob = React.useMemo(() => subjobs.find((sj) => sj.id === jobId), [jobId, subjobs]);

	React.useEffect(() => {
		if (showModal) {
			if (currentlyEditedRow) {
				initialize(new FormModel(currentlyEditedRow));
			} else {
				const fm = new FormModel();
				fm.workRequestId = jobId;
				initialize(fm);
			}
		} else {
			destroy();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentlyEditedRow, jobId, showModal]);

	const showRevenue = !!formDataCustomerId;
	const titleAndSubmitName = !!currentlyEditedRow ? 'Edit Billable Work' : 'Add New Billable Work';

	const canSubmitEdited = dirty && valid;
	const canSubmit = !!currentlyEditedRow ? canSubmitEdited : valid;

	const calculatedUnitPriceValue = React.useMemo(() => {
		if (convertedFormUnitPrice === null || formDataUnit === null) {
			return renderBillingCodePlaceholder();
		} else {
			return <span>{`$${convertedFormUnitPrice.toFixed(4)} per ${formDataUnit}`}</span>;
		}
	}, [convertedFormUnitPrice, formDataUnit, renderBillingCodePlaceholder]);

	const groupColor = React.useMemo(() => {
		return billingCodeOptions.find((bcg) => bcg.id === formDataBillingCodeId)?.groupColor;
	}, [billingCodeOptions, formDataBillingCodeId]);

	return (
		<CustomModal
			closeModal={closeModal}
			modalStyle="info"
			showModal={showModal}
			size="lg"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title={titleAndSubmitName}
			/>
			<CustomModal.Body>
				<div className={styles['add-billable-work-modal']}>
					<Field
						component={Dropdown}
						filterable={true}
						filterBy={['label']}
						fixed={false}
						hasBlankOption={true}
						id="workOrderId"
						label="Work Order"
						labelKey="label"
						name="workOrderId"
						onClear={onWorkOrderClear}
						options={workOrderOptions}
						placeholder="Select Work Order"
						valueKey="id"
						withCaret={true}
					/>

					<div className={styles['add-billable-work-modal__date']}>
						<Field
							component={DateInput}
							dateFormat={TimeFormat.DATE_ONLY}
							id="date"
							label="Date *"
							name="date"
							originalDateFormat={TimeFormat.DATE_ONLY}
							placeholderText="Select Date"
							showTimeSelect={true}
						/>
					</div>
					<div className={styles['add-billable-work-modal__multiple']}>
						<Field
							component={Input}
							fixed={true}
							id="work"
							isRequired={true}
							label="Work"
							name="work"
							placeholder="Add Work"
						/>
					</div>
					{currentlyEditedRow && currentlyEditedRow.dataType !== JobWorkSummaryDataType.STANDALONE
						&& formDataType !== formDataTypeValue ?
						<div className={styles['add-billable-work-modal']}>
							<Field
								component={Input}
								fixed={true}
								id="type"
								isRequired={true}
								label="Type"
								name="type"
								placeholder="Add Type"
							/>
							<Field
								component={Input}
								fixed={true}
								format={_formatTypeField(currentlyEditedRow.typeFieldType)}
								id="typeValue"
								isRequired={true}
								label="Type Value"
								name="typeValue"
								placeholder="Add Type Value"
							/>
						</div> :
						<div className={styles['add-billable-work-modal__multiple']}>
							<Field
								component={Input}
								fixed={true}
								id="type"
								isRequired={true}
								label="Type"
								name="type"
								placeholder="Add Type"
							/>
						</div>
					}
					<div className={styles['add-billable-work-modal__multiple']}>
						<FieldArray<{ originalUnits: Nullable<OriginalUnits>; }>
							component={AddBillableWorkModalDefinitionField}
							name="definitionFields"
							originalUnits={originalUnits}
						/>
					</div>
					<div className={styles['add-billable-work-modal__multiple']}>
						<FieldArray<{ originalUnits: Nullable<OriginalUnits>; }>
							component={AddBillableWorkModalInformationalField}
							name="informationalFields"
							originalUnits={originalUnits}
						/>
					</div>

					<Field
						component={Input}
						fixed={true}
						isRequired={true}
						label="Quantity"
						name="quantity"
						onValueChange={onQuantityChange}
						placeholder="Add Quantity"
						type="number"
					/>
					<Field
						component={Dropdown}
						defaultValue={undefined}
						disabled={false}
						filterable={true}
						filterBy={['id', 'label']}
						fixed={true}
						id="unit"
						label="Unit *"
						labelKey="label"
						name="unit"
						onClear={onUnitClear}
						onValueChange={onUnitChange}
						options={filteredQuantityUnitOptions}
						placeholder="Add Unit"
						valueKey="id"
						withCaret={true}
					/>
					<div>
						{
							isProject &&
							process.env.FTD_PROJECT !== 'true' &&
							<Field
								component={Dropdown}
								defaultValue={currentSubjob}
								filterable={true}
								filterBy={['jobCode']}
								fixed={true}
								isClearable={true}
								label="Sub-job"
								name="workRequestId"
								onChange={onJobChange}
								options={subjobs}
								renderMenuItem={_renderSubjobMenuItem}
								renderSelected={_renderSelectedSubjob}
								valueKey="id"
								withCaret={true}
							/>
						}
						{renderBillingCodeDropdown()}
						<div className={styles['add-billable-work-modal__billing-code-item']}>
							Group
							<div className={styles['add-billable-work-modal__billing-code-group']}>
								{groupColor && <ColorSquare color={groupColor} />}
								<Field
									component={Text}
									defaultValue={formDataCustomerId && !formDataGroup ? '-' : renderBillingCodePlaceholder()}
									id="group"
									name="group"
								/>
							</div>
						</div>
						<div className={styles['add-billable-work-modal__billing-code-item']}>
							Description
							<Field
								component={Text}
								defaultValue={renderBillingCodePlaceholder()}
								id="description"
								name="description"
							/>
						</div>
						<div className={styles['add-billable-work-modal__billing-code-item']}>
							<div>Unit price</div>
							<b>{calculatedUnitPriceValue}</b>
						</div>
						<div className={styles['add-billable-work-modal__billing-code-item']}>
							<div>
								Revenue
							</div>
							<b>
								{showRevenue
									? dollarFormatter.format(formDataRevenue ?? 0)
									: renderBillingCodePlaceholder()
								}
							</b>
						</div>
					</div>
					<div className={styles['add-billable-work-modal__comment']}>
						<Field
							className={styles['add-billable-work-modal']}
							component={Textarea}
							id="comment"
							label="Comment"
							maxCharacters={750}
							name="comment"
							placeholder="Add Comment"
							rows={16}
							showMaxCharactersLabel={true}
						/>
					</div>
				</div>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={closeModal}
					style="secondary"
				/>
				<SubmitButton
					disabled={!canSubmit}
					label={titleAndSubmitName}
					onClick={handleSubmit(onSubmit)}
					variant="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const {
		unit: formDataUnit,
		quantity: formDataQuantity,
		billingCodeId: formDataBillingCodeId,
		revenue: formDataRevenue,
		unitPrice: formDataUnitPrice,
		customerId: formDataCustomerId,
		group: formDataGroup,
		workRequestId: formWorkRequestId,
		workOrderId: formWorkOrderId,
		type: formDataType,
		typeValue: formDataTypeValue,
		originalUnits: originalUnits,
	} = (getFormData(state) as FormModel) ?? ({} as Partial<FormModel>);

	return {
		formDataUnit: formDataUnit ? formDataUnit.replace('/', '_') : null,
		formDataBillingCodeId,
		formDataQuantity,
		formDataRevenue,
		formDataUnitPrice,
		formDataCustomerId,
		formDataGroup,
		formWorkRequestId,
		formWorkOrderId,
		formDataType,
		formDataTypeValue,
		originalUnits,
	};
}

const formConnector = reduxForm<FormModel>({
	form: FORMS.WORK_SUMMARY_BILLABLE_WORK,
	validate: FormModel.validate,
});

function mapDispatchToProps() {
	return {
		createJobWorkSummary: JobActions.createJobWorkSummary,
		editJobWorkSummary: JobActions.editJobWorkSummary,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	formConnector,
	connector
);

export default enhance(AddBillableWorkModal);
